import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModuleRef } from 'src/app/shared/module-search/module-search.component';
import {
  FirmwareUpdateApiService,
  FirmwareVersion,
} from 'src/app/shared/services/firmware-update-api/firmware-update-api.service';
import {
  FirmwareService,
  Module,
} from '../../modules/services/firmware/firmware.service';
import { ReleaseFirmwareVersion } from '../services/release.service';
@Component({
  selector: 'dcc-app-selector',
  templateUrl: './app-selector.component.html',
  styleUrls: ['./app-selector.component.scss'],
})
export class AppSelectorComponent implements OnInit {
  @Input() isUpdate = false;
  @Input() selectedModules: ModuleRef[] = [];
  @Input() selectedFirmwareVersions: ReleaseFirmwareVersion[] = [];
  @Output() updateFirmware: EventEmitter<ReleaseFirmwareVersion[]> =
    new EventEmitter<ReleaseFirmwareVersion[]>();
  @Output() updateModules: EventEmitter<ModuleRef[]> = new EventEmitter<
    ModuleRef[]
  >();

  modules: ModuleRef[] = [];
  filteredModules: ModuleRef[];
  deviceSelection: ModuleRef[] = [];
  versionAndNameSelection: ModuleRef[] = [];
  selectedDeviceType: string;
  lcFlex: string[] = ['SF300', 'SF300W', 'SF200', 'SF64', 'SF1'];
  otto: string[] = ['SF500', 'SF400', 'SF400V2', 'SF400V2W', 'SF300V2'];
  addNewModule = false;

  firmwareVersions: ReleaseFirmwareVersion[] = [];
  filteredFirmwareVersions: ReleaseFirmwareVersion[];
  firmwareDeviceSelection: ReleaseFirmwareVersion[] = [];
  firmwareVersionsSelection: ReleaseFirmwareVersion[] = [];

  selectedFirmwareDeviceType: string;
  addNewFirmware = false;

  constructor(
    private moduleService: FirmwareService,
    private firmwareUpdateService: FirmwareUpdateApiService,
  ) {}

  async ngOnInit() {
    await this.loadModules();
    await this.loadFirmwareVersions();
    this.createDeviceSelection();

    if (this.isUpdate) {
      this.filterOutSelectedModules();
      this.filterOutSelectedFirmwareVersions();
    }

    this.filterDisplayNames();
    this.filterFirmwareVersionDisplayNames();
  }

  async loadFirmwareVersions(): Promise<void> {
    const versions = await this.firmwareUpdateService.getFirmwareVersions();

    versions.forEach((firmware) => {
      this.firmwareVersions.push(
        this.formatFirmwareVersions(firmware, firmware.platformType),
      );

      firmware.platformType.toLowerCase() === 'lcflex'
        ? this.createFirmwareDeviceTypes(this.lcFlex, firmware)
        : this.createFirmwareDeviceTypes(this.otto, firmware);
    });

    this.firmwareVersions.push(this.createDoNotUse('LcFlex'));
    this.firmwareVersions.push(this.createDoNotUse('Otto'));

    this.lcFlex.forEach((p) =>
      this.firmwareVersions.push(this.createDoNotUse(p)),
    );
    this.otto.forEach((p) =>
      this.firmwareVersions.push(this.createDoNotUse(p)),
    );

    this.filteredFirmwareVersions = this.firmwareVersions;
  }

  async loadModules(): Promise<void> {
    (await this.moduleService.getModules()).forEach((module) => {
      this.modules.push({
        name: module.name,
        version: module.version,
        platform: module.platform,
        configuration: module?.configuration,
        configurationSchema: module?.configurationSchema,
      } as ModuleRef);
      module.platform.toLowerCase() === 'lcflex'
        ? this.createDeviceTypes(this.lcFlex, module)
        : this.createDeviceTypes(this.otto, module);
    });
    this.filteredModules = this.modules;
  }

  onDeviceTypeSelected(platform: string) {
    this.selectedDeviceType = platform;
    this.versionAndNameSelection = this.filteredModules.filter(
      (m) => m.platform === platform,
    );
  }

  onModuleSelected(module: ModuleRef) {
    module = this.modules.find(
      (m) =>
        m.name === module.name &&
        m.version === module.version &&
        m.platform === this.selectedDeviceType,
    );
    this.selectedModules.push(module);
    this.filterModulesByNameAndPlatform(module.name, module.platform);
    this.filterDisplayNames();
    this.resetModuleSearches();
    this.updateModules.emit(this.selectedModules);
  }

  removeModule(module: ModuleRef) {
    this.selectedModules = this.filterModules(this.selectedModules, module);
    this.filteredModules = [
      ...this.filteredModules,
      ...this.modules.filter(
        (m) => m.name === module.name && m.platform === module.platform,
      ),
    ];
    if (module.platform === 'LcFlex' || module.platform === 'Otto') {
      module.platform.toLowerCase() === 'lcflex'
        ? this.addDeviceTypes(this.lcFlex, module as Module)
        : this.addDeviceTypes(this.otto, module as Module);
    }
    this.filteredModules = JSON.parse(JSON.stringify(this.filteredModules));
    this.filterDisplayNames();
    this.updateModules.emit(this.selectedModules);
  }

  onFirmwareDeviceTypeSelected(platformType: string) {
    this.selectedFirmwareDeviceType = platformType;
    this.firmwareVersionsSelection = this.filteredFirmwareVersions.filter(
      (m) => m.platform === platformType,
    );
  }

  onFirmwareSelected(firmwareVersion: ReleaseFirmwareVersion) {
    firmwareVersion = this.firmwareVersions.find(
      (m) =>
        m.name === firmwareVersion.name &&
        m.version === firmwareVersion.version &&
        m.platform === this.selectedFirmwareDeviceType,
    );
    this.selectedFirmwareVersions.push(firmwareVersion);
    this.filterFirmwareVersionsByNameAndPlatform(
      firmwareVersion.name,
      firmwareVersion.platform,
    );
    this.filterFirmwareVersionDisplayNames();
    this.resetFirmwareSearch();
    this.updateFirmware.emit(this.selectedFirmwareVersions);
  }

  removeFirmwareVersion(firmwareVersion: ReleaseFirmwareVersion) {
    this.selectedFirmwareVersions = this.filterFirmwareVersions(
      this.selectedFirmwareVersions,
      firmwareVersion,
    );
    this.filteredFirmwareVersions = [
      ...this.filteredFirmwareVersions,
      ...this.firmwareVersions.filter(
        (m) =>
          m.name === firmwareVersion.name &&
          m.platform === firmwareVersion.platform,
      ),
    ];
    this.filteredFirmwareVersions = JSON.parse(
      JSON.stringify(this.filteredFirmwareVersions),
    );
    this.filterDisplayNames();
    this.updateFirmware.emit(this.selectedFirmwareVersions);
  }

  addModule(): void {
    this.addNewModule = true;
  }

  addFirmware(): void {
    this.addNewFirmware = true;
  }

  shouldShowAddFirmware(): boolean {
    if (this.selectedFirmwareVersions.length > 0 && !this.addNewFirmware) {
      return false;
    } else if (this.addNewFirmware) {
      return false;
    } else {
      return true;
    }
  }

  showConfigurations(module: ModuleRef) {
    return module.configurationSchema || module.configuration;
  }

  onConfigurationUpload(moduleRef: ModuleRef, index: number, event: Event) {
    this.selectedModules[index].configuration = (
      event.target as HTMLInputElement
    ).value;
    this.isJsonValid(moduleRef);
  }

  isJsonValid(module: ModuleRef): boolean {
    try {
      if (module.configuration && module.configurationSchema) {
        JSON.parse(module.configuration);
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  private createDoNotUse(platform: string): ReleaseFirmwareVersion {
    const releaseFirmwareVersion: ReleaseFirmwareVersion = {
      build: '0',
      version: '0',
      name: 'Do not use',
      platform: platform,
      firmwareId: '00000000-0000-0000-0000-000000000000',
      hardwarePlatform: null,
    };

    return releaseFirmwareVersion;
  }

  private createFirmwareDeviceTypes(
    platform: string[],
    firmware: FirmwareVersion,
  ): void {
    platform.forEach((platform) => {
      this.firmwareVersions.push(
        this.formatFirmwareVersions(firmware, platform),
      );
    });
  }

  private formatFirmwareVersions(
    firmwareVersion: FirmwareVersion,
    platform: string,
  ): ReleaseFirmwareVersion {
    const releaseFirmwareVersion: ReleaseFirmwareVersion = {
      build: firmwareVersion.build,
      version: firmwareVersion.version,
      name: firmwareVersion.name,
      platform: platform,
      firmwareId: firmwareVersion.id,
      hardwarePlatform: null,
    };
    return releaseFirmwareVersion;
  }

  private filterOutSelectedModules(): void {
    this.selectedModules?.forEach((m) =>
      this.filterModulesByNameAndPlatform(m.name, m.platform),
    );
  }

  private createDeviceTypes(platform: string[], module: Module): void {
    platform.forEach((platform) => {
      this.modules.push({
        name: module.name,
        version: module.version,
        platform: platform,
        hardwarePlatform: module?.hardwarePlatform,
        configuration: module?.configuration,
        configurationSchema: module?.configurationSchema,
      } as ModuleRef);
    });
  }

  private createDeviceSelection() {
    this.createModuleDeviceSelection();
    this.createFirmwareDeviceSelection();
  }

  private createModuleDeviceSelection(): void {
    this.deviceSelection.push(this.createModuleRef('LcFlex'));
    this.deviceSelection.push(this.createModuleRef('Otto'));
    this.lcFlex.forEach((p) =>
      this.deviceSelection.push(this.createModuleRef(p)),
    );
    this.otto.forEach((p) =>
      this.deviceSelection.push(this.createModuleRef(p)),
    );
  }

  private createFirmwareDeviceSelection(): void {
    this.firmwareDeviceSelection.push(this.createFirmwareVersion('LcFlex'));
    this.firmwareDeviceSelection.push(this.createFirmwareVersion('Otto'));
    this.lcFlex.forEach((p) =>
      this.firmwareDeviceSelection.push(this.createFirmwareVersion(p)),
    );
    this.otto.forEach((p) =>
      this.firmwareDeviceSelection.push(this.createFirmwareVersion(p)),
    );
  }

  private createModuleRef(platform: string): ModuleRef {
    return { name: '', version: '', platform: platform };
  }

  private createFirmwareVersion(platform: string): ReleaseFirmwareVersion {
    return {
      firmwareId: '',
      build: '',
      hardwarePlatform: '',
      name: '',
      version: '',
      platform: platform,
    };
  }

  private resetModuleSearches() {
    this.selectedDeviceType = null;
    this.addNewModule = false;
  }

  private filterModulesByNameAndPlatform(name: string, platform: string) {
    this.filteredModules = this.filteredModules.filter(
      (m) => !(m.name === name && m.platform === platform),
    );
    if (platform === 'LcFlex' || platform === 'Otto') {
      platform.toLowerCase() === 'lcflex'
        ? this.removeDeviceTypes(this.lcFlex, name, platform)
        : this.removeDeviceTypes(this.otto, name, platform);
    }
  }

  private removeDeviceTypes(
    platformType: string[],
    name: string,
    platform: string,
  ) {
    this.filteredModules = this.filteredModules.filter(
      (m) => !(m.name === name && platformType.includes(m.platform)),
    );
  }

  private filterDisplayNames(): void {
    this.filterForUniqueNameAndVersion();
    this.filterForUniquePlatform();
  }

  private filterForUniqueNameAndVersion(): void {
    this.versionAndNameSelection = this.filteredModules.filter(
      (v, i, a) =>
        a.findIndex((v2) =>
          ['name', 'version'].every((k) => v2[k] === v[k]),
        ) === i,
    );
    this.versionAndNameSelection = JSON.parse(
      JSON.stringify(this.versionAndNameSelection),
    );
  }

  private filterForUniquePlatform(): void {
    this.deviceSelection = this.filteredModules.filter(
      (v, i, a) => a.findIndex((v2) => v2.platform === v.platform) === i,
    );
    this.deviceSelection = JSON.parse(JSON.stringify(this.deviceSelection));
  }

  private addDeviceTypes(platform: string[], module: Module): void {
    platform.forEach((platform) => {
      this.filteredModules.push({
        name: module.name,
        version: module.version,
        platform: platform,
        hardwarePlatform: module?.hardwarePlatform,
        configuration: module?.configuration,
        configurationSchema: module?.configurationSchema,
      } as ModuleRef);
    });
  }

  private filterModules(modules: ModuleRef[], module: ModuleRef): ModuleRef[] {
    return modules.filter(
      (m) =>
        !(
          m.name === module.name &&
          m.version === module.version &&
          m.platform === module.platform
        ),
    );
  }

  private resetFirmwareSearch() {
    this.selectedFirmwareDeviceType = null;
    this.addNewFirmware = false;
  }

  private filterFirmwareVersionsByNameAndPlatform(
    name: string,
    platformType: string,
  ) {
    this.filteredFirmwareVersions = this.filteredFirmwareVersions.filter(
      (f) => !(f.name === name && f.platform === platformType),
    );
  }

  private filterFirmwareVersions(
    firmwareVersions: ReleaseFirmwareVersion[],
    firmwareVersion: ReleaseFirmwareVersion,
  ): ReleaseFirmwareVersion[] {
    return firmwareVersions.filter(
      (m) =>
        !(
          m.name === firmwareVersion.name &&
          m.version === firmwareVersion.version &&
          m.platform === firmwareVersion.platform
        ),
    );
  }

  private filterFirmwareVersionDisplayNames(): void {
    this.filterForUniqueFirmwareNameAndVersion();
    this.filterForUniqueFirmwarePlatform();
  }

  private filterForUniqueFirmwarePlatform(): void {
    this.firmwareDeviceSelection = this.filteredFirmwareVersions.filter(
      (v, i, a) => a.findIndex((v2) => v2.platform === v.platform) === i,
    );
    this.firmwareDeviceSelection = JSON.parse(
      JSON.stringify(this.firmwareDeviceSelection),
    );
  }

  private filterForUniqueFirmwareNameAndVersion(): void {
    this.firmwareVersionsSelection = this.filteredFirmwareVersions.filter(
      (v, i, a) =>
        a.findIndex((v2) =>
          ['name', 'version'].every((k) => v2[k] === v[k]),
        ) === i,
    );
    this.firmwareVersionsSelection = JSON.parse(
      JSON.stringify(this.versionAndNameSelection),
    );
  }

  private filterOutSelectedFirmwareVersions(): void {
    this.selectedFirmwareVersions?.forEach((f) =>
      this.filterFirmwareVersionsByNameAndPlatform(f.name, f.platform),
    );
  }
}
